import { useCallback, useEffect, useRef, useState } from 'react';

// Live voice interview: mic -> backend socket -> Deepgram Voice Agent, and the host's audio back.
// status: idle | connecting | listening | thinking | speaking | ended | error
export default function useVoiceInterview(url, { onText } = {}) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [level, setLevel] = useState(0);

  const ws = useRef(null);
  const micCtx = useRef(null);
  const outCtx = useRef(null);
  const stream = useRef(null);
  const node = useRef(null);
  const playing = useRef([]);
  const nextTime = useRef(0);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stopPlayback = () => {
    playing.current.forEach((s) => { try { s.stop(); } catch (e) { /* already stopped */ } });
    playing.current = [];
    nextTime.current = 0;
  };

  const playChunk = (buf) => {
    const ctx = outCtx.current;
    if (!ctx) return;
    const pcm = new Int16Array(buf);
    if (!pcm.length) return;
    const audio = ctx.createBuffer(1, pcm.length, 24000);
    const ch = audio.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 0x8000;
    const src = ctx.createBufferSource();
    src.buffer = audio;
    src.connect(ctx.destination);
    const at = Math.max(ctx.currentTime + 0.02, nextTime.current);
    src.start(at);
    nextTime.current = at + audio.duration;
    playing.current.push(src);
    src.onended = () => { playing.current = playing.current.filter((s) => s !== src); };
  };

  const cleanup = useCallback(() => {
    stopPlayback();
    try { node.current && node.current.disconnect(); } catch (e) { /* ignore */ }
    try { stream.current && stream.current.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    try { micCtx.current && micCtx.current.close(); } catch (e) { /* ignore */ }
    try { outCtx.current && outCtx.current.close(); } catch (e) { /* ignore */ }
    micCtx.current = outCtx.current = stream.current = node.current = null;
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setStatus('connecting');
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch (e) {
      setStatus('error');
      setError('We need microphone access for a voice interview. Allow it in your browser, or switch to chat.');
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    micCtx.current = new AC();
    outCtx.current = new AC();
    try { await outCtx.current.resume(); await micCtx.current.resume(); } catch (e) { /* ignore */ }
    try {
      await micCtx.current.audioWorklet.addModule('/interview-mic-worklet.js');
    } catch (e) {
      cleanup();
      setStatus('error');
      setError("This browser can't run the voice interview. Try Chrome or Safari, or switch to chat.");
      return;
    }
    const source = micCtx.current.createMediaStreamSource(stream.current);
    node.current = new AudioWorkletNode(micCtx.current, 'stagehead-mic');
    source.connect(node.current);

    const sock = new WebSocket(url);
    sock.binaryType = 'arraybuffer';
    ws.current = sock;

    node.current.port.onmessage = (e) => {
      setLevel(e.data.level || 0);
      if (sock.readyState === WebSocket.OPEN) sock.send(e.data.pcm);
    };

    sock.onopen = () => setStatus('listening');
    sock.onmessage = (e) => {
      if (typeof e.data !== 'string') {
        playChunk(e.data);
        return;
      }
      let m;
      try { m = JSON.parse(e.data); } catch (err) { return; }
      switch (m.type) {
        case 'ConversationText':
          onTextRef.current && onTextRef.current({ role: m.role, text: m.content });
          break;
        case 'UserStartedSpeaking':
          stopPlayback();
          setStatus('listening');
          break;
        case 'AgentThinking':
          setStatus('thinking');
          break;
        case 'AgentStartedSpeaking':
          setStatus('speaking');
          break;
        case 'AgentAudioDone':
          setStatus('listening');
          break;
        case 'Error':
          setError(m.description || 'The voice service had a problem.');
          break;
        default:
          break;
      }
    };
    sock.onerror = () => setError('The connection dropped. You can reconnect and pick up where you left off.');
    sock.onclose = () => {
      cleanup();
      setStatus((s) => (s === 'error' ? s : 'ended'));
    };
  }, [url, cleanup]);

  const stop = useCallback(() => {
    const sock = ws.current;
    if (sock && sock.readyState === WebSocket.OPEN) {
      sock.send(JSON.stringify({ type: 'End' }));
      sock.close();
    }
    cleanup();
    setStatus('ended');
  }, [cleanup]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (node.current) node.current.port.postMessage({ muted: next });
      return next;
    });
  }, []);

  useEffect(() => () => {
    try { ws.current && ws.current.close(); } catch (e) { /* ignore */ }
    cleanup();
  }, [cleanup]);

  return { status, error, muted, level, start, stop, toggleMute };
}
